// The questionnaire as data. The design's own framing: "the chrome is constant for all
// twelve questions" — so every screen is one QuestionDef rendered by one engine, not a
// bespoke component. Branching lives in each step's `when(answers)` predicate; the
// visible path is derived from answers, which is why we can honestly show TIME remaining
// (not "step N of 12") — the count isn't knowable until the answers decide it.
//
// Q7b ("invite your therapist" / therapistInviteEmail) is intentionally NOT here — cut
// from Phase 3 (no backend: no column, endpoint, viewer, or revocation, and it shares
// PHI pre-account). It returns as a post-account "Invite your therapist" action later.
import type { Answers } from './session';
import { INSURANCE_CARRIERS, INSURANCE_OTHER, UK_INSURANCE_PLANS } from './insurance';
import { providerNoun } from './noun';

export type Option = { value: string; label: string; sublabel?: string };

// A field that is either static or depends on the answers so far. Used for country-aware
// copy: once the postcode step sets `country`, the provider noun and the insurance list
// resolve to the patient's market (see resolveQuestion + ptNoun below).
export type Dyn<T> = T | ((a: Answers) => T);

export type QuestionType =
  | 'bodyArea' // 2-col card grid with body icons, tap-advances
  | 'detail' // 2 cards with descriptions (upper/lower back)
  | 'single' // vertical single-select, tap-advances
  | 'surgery' // the catalogue's surgeries for the area + "Another surgery" (free text)
  | 'surgeryDate' // date + "not scheduled", needs Continue
  | 'pain' // 0–10 scale, needs Continue
  | 'goal' // tinted engagement moment: picks + optional free text
  | 'zip' // text → derived state, needs Continue
  | 'email'; // email + consent + disclaimer, submit

export interface QuestionDef {
  id: string;
  field: string; // the answers[] key it writes
  type: QuestionType;
  eyebrow?: Dyn<string>;
  title: Dyn<string>;
  subtitle?: Dyn<string>;
  options?: Dyn<Option[]>;
  tinted?: boolean; // teal-tint surface (the goal moment)
  when?: (a: Answers) => boolean; // included in the path only if true
}

// A QuestionDef with every Dyn<> field resolved against the current answers — what the
// renderer actually draws. The engine calls resolveQuestion() before rendering each step.
export interface ResolvedQuestion {
  id: string;
  field: string;
  type: QuestionType;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  options?: Option[];
  tinted?: boolean;
}

function dyn<T>(v: Dyn<T> | undefined, a: Answers): T | undefined {
  return typeof v === 'function' ? (v as (a: Answers) => T)(a) : v;
}

export function resolveQuestion(q: QuestionDef, a: Answers): ResolvedQuestion {
  return {
    id: q.id,
    field: q.field,
    type: q.type,
    tinted: q.tinted,
    eyebrow: dyn(q.eyebrow, a),
    title: dyn(q.title, a) as string,
    subtitle: dyn(q.subtitle, a),
    options: dyn(q.options, a),
  };
}

// Market-aware provider noun (shared with the Result screen + WP directory via lib/noun).
// UK = "physiotherapist" (one national HCPC registration, treats anywhere in the UK);
// US/default = "physical therapist". Never a bare "therapist". Country comes from the postcode
// step, which now runs before the matching block, so these steps can resolve it.
const isGB = (a: Answers) => a.country === 'GB';
const ptNoun = (a: Answers, plural = false) => providerNoun(a.country as string | undefined, plural);

export const BODY_AREAS = [
  'Neck', 'Back', 'Shoulder', 'Elbow', 'Wrist',
  'Hand', 'Hip', 'Knee', 'Foot', 'Ankle',
] as const;

// True when the matching block (payment / insurance / gender) applies.
const seekingOrHasPT = (a: Answers) => a.intent === 'WANT_PT' || a.intent === 'HAS_PT';

export const QUESTIONS: QuestionDef[] = [
  {
    id: 'bodyPart',
    field: 'bodyPart',
    type: 'bodyArea',
    eyebrow: "Let's build your plan",
    title: 'What part of your body would you like to focus on?',
    subtitle: "Pick the area that's giving you trouble. You can change this later.",
    options: BODY_AREAS.map((b) => ({ value: b, label: b })),
  },
  {
    id: 'bodyPartDetail',
    field: 'bodyPartDetail',
    type: 'detail',
    title: 'Upper or lower back?',
    subtitle: "They're different plans, so it's worth the extra tap.",
    options: [
      { value: 'Upper', label: 'Upper', sublabel: 'From the base of your neck down to the bottom of your rib cage' },
      { value: 'Lower', label: 'Lower', sublabel: 'From the bottom of your rib cage down to the top of your buttocks' },
    ],
    when: (a) => a.bodyPart === 'Back',
  },
  {
    id: 'careType',
    field: 'careType',
    type: 'single',
    title: 'Is this after surgery, an injury, or something ongoing?',
    subtitle: 'This decides which plan template you start from.',
    options: [
      { value: 'SURGERY', label: 'After surgery', sublabel: 'Scheduled or already done' },
      { value: 'INJURY', label: 'An injury', sublabel: 'A specific moment it started' },
      { value: 'CHRONIC', label: 'Ongoing or chronic', sublabel: "It's been there a while" },
      { value: 'UNSURE', label: "I'm not sure", sublabel: "We'll start general and adjust" },
    ],
  },
  {
    id: 'surgeryName',
    field: 'surgeryName',
    type: 'surgery',
    title: 'Which surgery?',
    // Honest for both outcomes of "Another surgery": long ago gets the standard plan; recent
    // gets no exercise plan until a person has set one up (the API's post-op guard).
    subtitle: "If yours isn't listed, choose the last option and we'll help you get the right plan.",
    // No options here: the list is the surgery catalogue for the body area already chosen,
    // loaded by the step itself (lib/surgeries.ts), ending in "Another surgery".
    when: (a) => a.careType === 'SURGERY',
  },
  {
    id: 'surgeryDate',
    field: 'surgeryDate',
    type: 'surgeryDate',
    title: 'When was your surgery?',
    subtitle: 'Your plan starts at the right phase for where you are.',
    when: (a) => a.careType === 'SURGERY',
  },
  {
    id: 'symptomDuration',
    field: 'symptomDuration',
    type: 'single',
    title: 'How long have you been dealing with this?',
    subtitle: 'A rough answer is fine.',
    options: [
      { value: 'Less than a week', label: 'Less than a week' },
      { value: 'One to four weeks', label: 'One to four weeks' },
      { value: 'One to three months', label: 'One to three months' },
      { value: 'Three to six months', label: 'Three to six months' },
      { value: 'More than six months', label: 'More than six months' },
    ],
  },
  {
    id: 'painLevel',
    field: 'painLevel',
    type: 'pain',
    title: "How's the pain right now?",
    subtitle: 'Right now, not at its worst. We ask again each week to show you the trend.',
  },
  {
    id: 'goal',
    field: 'goal',
    type: 'goal',
    tinted: true,
    eyebrow: 'The part that matters',
    title: 'What do you want to get back to?',
    subtitle: "We'll put this at the top of your plan and hand it back to you on the days you'd rather skip.",
    options: [
      { value: 'Reduce pain', label: 'Reduce pain', sublabel: 'Get through the day easier' },
      { value: 'Regain motion', label: 'Regain motion', sublabel: 'Bend, reach, move freely' },
      { value: 'Rebuild strength', label: 'Rebuild strength', sublabel: 'Stairs, lifting, standing' },
      { value: 'Return to my sport', label: 'Return to my sport', sublabel: 'Back on the court or trail' },
    ],
  },
  {
    // Asked before the matching block so we know the market (US/UK) from the postcode and
    // can use the right provider noun and insurance list downstream. Deliberately NOT the
    // very first screen: the marketing deep-link (?prefill=) seeds bodyPart+careType and
    // jumps past them, which only works while those stay at the front (see prefill.ts).
    id: 'zip',
    field: 'zip',
    type: 'zip',
    title: 'Where are you?',
    subtitle: 'Care options depend on where you are, so this tells us who can treat you and how booking works.',
  },
  {
    id: 'intent',
    field: 'intent',
    type: 'single',
    title: 'How do you want to approach this?',
    subtitle: (a) =>
      `Every answer gets the same plan. This only tells us whether to look for ${ptNoun(a, true)}.`,
    options: (a) => [
      { value: 'HAS_PT', label: `I already have a ${ptNoun(a)}`, sublabel: 'Your plan works alongside them' },
      { value: 'WANT_PT', label: 'Help me find one', sublabel: 'A few more questions, then matches' },
      { value: 'SELF', label: "I'm doing this on my own", sublabel: 'Straight to your plan' },
      { value: 'UNSURE', label: "I'm not sure yet", sublabel: 'You can decide later' },
    ],
  },
  {
    id: 'paymentPreference',
    field: 'paymentPreference',
    type: 'single',
    title: 'How would you like to pay for therapy?',
    subtitle: (a) => `Separate from your Expy plan. This is about the ${ptNoun(a)}'s bill.`,
    options: (a) => [
      { value: 'INSURANCE', label: 'Through my insurance', sublabel: `We’ll rank ${ptNoun(a, true)} who take your plan` },
      { value: 'CASH', label: 'Out of pocket', sublabel: 'Cash rates, no claims' },
      { value: 'NO_PREFERENCE', label: 'No preference', sublabel: 'Show me everyone' },
    ],
    when: seekingOrHasPT,
  },
  {
    id: 'insuranceProvider',
    field: 'insuranceProvider',
    type: 'single',
    title: (a) => (isGB(a) ? 'Which insurer do you have?' : 'Which insurance do you have?'),
    subtitle: 'Just the provider. No member ID. That comes later, when you actually book.',
    options: (a) => [
      ...(isGB(a) ? UK_INSURANCE_PLANS : INSURANCE_CARRIERS).map((c) => ({ value: c, label: c })),
      { value: INSURANCE_OTHER, label: INSURANCE_OTHER },
    ],
    when: (a) => seekingOrHasPT(a) && a.paymentPreference === 'INSURANCE',
  },
  {
    id: 'therapistGenderPreference',
    field: 'therapistGenderPreference',
    type: 'single',
    title: (a) => `Would you prefer a man or a woman as your ${ptNoun(a)}?`,
    subtitle: 'Some people feel more comfortable one way or the other. If that’s you, we’ll keep it in mind.',
    // No non-binary option, deliberately. Matching is a person reading these
    // answers against a roster that is currently 14 therapists, all of whom
    // record themselves as male or female (Users.gender is MALE|FEMALE|OTHER),
    // so it is a preference we could not act on. Offering a choice we cannot
    // honour is worse than not offering it; anyone it matters to is better
    // served by the concierge conversation than by a dropdown that goes nowhere.
    // Historical answers keep the value - the DB enum and the API are unchanged.
    options: [
      { value: 'NO_PREFERENCE', label: 'No preference' },
      { value: 'FEMALE', label: 'A woman' },
      { value: 'MALE', label: 'A man' },
    ],
    when: seekingOrHasPT,
  },
  {
    id: 'email',
    field: 'email',
    type: 'email',
    title: 'Where should we send your plan?',
    subtitle: "Last one. You'll see your plan on the next screen either way. The email is so you can come back to it.",
  },
];

// The visible path for a given set of answers.
export function visibleSteps(a: Answers): QuestionDef[] {
  return QUESTIONS.filter((q) => (q.when ? q.when(a) : true));
}

// Rough seconds a step costs — tap-to-advance screens are ~8s, typing/entry more.
function stepCost(q: QuestionDef): number {
  switch (q.type) {
    case 'pain':
      return 10;
    case 'goal':
      return 18;
    case 'zip':
      return 14;
    case 'email':
      return 22;
    case 'surgeryDate':
      return 14;
    default:
      return 8;
  }
}

// Honest "time remaining" for the current position, given the branch the answers imply.
// Returns a short label the Shell shows next to the clock.
export function timeRemainingLabel(a: Answers, currentIndex: number): string {
  const steps = visibleSteps(a);
  const remaining = steps.slice(currentIndex + 1);
  if (remaining.length === 0) return 'Almost done';
  const secs = remaining.reduce((sum, q) => sum + stepCost(q), 0);
  if (secs <= 20) return 'Almost done';
  if (secs < 75) return `About ${Math.round(secs / 5) * 5} sec left`;
  const mins = secs / 60;
  const rounded = Math.round(mins * 2) / 2; // nearest half-minute
  return `About ${rounded % 1 === 0 ? rounded : rounded.toFixed(1)} min left`;
}

// Progress bar fraction (0–1).
export function progressFraction(a: Answers, currentIndex: number): number {
  const total = visibleSteps(a).length;
  return total ? Math.min(1, (currentIndex + 1) / total) : 0;
}

// Analytics name for a question, sent to GA4 as `funnel_step` (no BAA, so no PHI).
// It names the question, never the answer. A shared generic key is NOT enough to
// hide a clinical branch (the position of `condition_detail` before or after
// `care_type` still reveals back pain vs surgery), so Questionnaire.tsx only sends
// steps that have no `when` predicate. Unknown ids map to 'other' rather than
// leaking a new id by default.
const STEP_KEYS: Record<string, string> = {
  bodyPart: 'body_area',
  bodyPartDetail: 'condition_detail',
  careType: 'care_type',
  surgeryName: 'condition_detail',
  surgeryDate: 'condition_detail',
  symptomDuration: 'duration',
  painLevel: 'pain_level',
  goal: 'goal',
  zip: 'location',
  intent: 'intent',
  paymentPreference: 'payment',
  insuranceProvider: 'insurance',
  therapistGenderPreference: 'therapist_preference',
  email: 'email',
};

export function stepKey(questionId: string): string {
  return STEP_KEYS[questionId] ?? 'other';
}
